export interface HelcimCardDetails {
  cardToken: string | null;
  cardLast4: string | null;
  cardBrand: string | null;
}

/**
 * Unwrap HelcimPay.js eventMessage (double-stringified JSON) into a flat object.
 */
function unwrapEventMessage(
  eventMessage: unknown,
): Record<string, unknown> | null {
  let msg = eventMessage;
  for (let i = 0; i < 2; i++) {
    if (typeof msg === "string") {
      try {
        msg = JSON.parse(msg);
      } catch {
        break;
      }
    }
  }
  if (typeof msg !== "object" || msg === null) return null;
  return msg as Record<string, unknown>;
}

/**
 * Extract cardToken from a HelcimPay.js eventMessage.
 */
export function extractCardToken(eventMessage: unknown): string | null {
  const obj = unwrapEventMessage(eventMessage);
  if (!obj) return null;
  const outer = obj.data as Record<string, unknown> | undefined;
  const inner = outer?.data as Record<string, unknown> | undefined;
  return (inner?.cardToken as string) ?? (outer?.cardToken as string) ?? null;
}

const CARD_BRAND_MAP: Record<string, string> = {
  MC: "Mastercard",
  VISA: "Visa",
  VI: "Visa",
  AMEX: "American Express",
  AX: "American Express",
  DISC: "Discover",
  DC: "Discover",
  DINERS: "Diners Club",
  DN: "Diners Club",
  JCB: "JCB",
  UNIONPAY: "UnionPay",
  INTERAC: "Interac",
};

export function normalizeCardBrand(raw: string): string {
  return CARD_BRAND_MAP[raw.toUpperCase()] ?? raw;
}

/**
 * Extract card details (token, last 4, brand) from a HelcimPay.js eventMessage.
 *
 * Helcim returns cardNumber as a masked string like "5454XXXXXXXX5454" —
 * we take the last 4 characters. cardType is a short code like "MC", "VISA".
 */
export function extractCardDetails(eventMessage: unknown): HelcimCardDetails {
  const obj = unwrapEventMessage(eventMessage);
  if (!obj) return { cardToken: null, cardLast4: null, cardBrand: null };
  const outer = obj.data as Record<string, unknown> | undefined;
  const inner = outer?.data as Record<string, unknown> | undefined;
  const data = inner ?? outer;

  const cardToken = (data?.cardToken as string) ?? null;
  const cardNumber = (data?.cardNumber as string) ?? null;
  const cardLast4 =
    cardNumber && cardNumber.length >= 4 ? cardNumber.slice(-4) : null;
  const rawType = (data?.cardType as string) ?? null;
  const cardBrand = rawType ? normalizeCardBrand(rawType) : null;

  return { cardToken, cardLast4, cardBrand };
}
